export type ApiErrorDetails = {
  body: unknown;
  code: string | null;
  error: string | null;
  message: string;
  messages: string[];
  requestId: string | null;
  statusCode: number | null;
};

type ApiErrorShape = {
  code?: unknown;
  details?: unknown;
  error?: unknown;
  message?: unknown;
  messages?: unknown;
  requestId?: unknown;
  status?: unknown;
  statusCode?: unknown;
};

const defaultErrorMessage = 'Request failed.';

function asObject(value: unknown): ApiErrorShape | null {
  return value && typeof value === 'object' ? value as ApiErrorShape : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function extractMessages(message: unknown): string[] {
  if (Array.isArray(message)) {
    return message.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
  }

  const text = asString(message);
  return text ? [text] : [];
}

function extractDetailMessages(details: unknown): string[] {
  const detailObject = asObject(details);
  return extractMessages(detailObject?.messages);
}

function unwrapGeneratedError(error: unknown): unknown {
  const body = asObject(error);

  if (body && body.error && typeof body.error === 'object') {
    return body.error;
  }

  return error;
}

export function normalizeApiError(error: unknown, fallback = defaultErrorMessage): ApiErrorDetails {
  if (typeof error === 'string') {
    return {
      body: error,
      code: null,
      error: null,
      message: error || fallback,
      messages: [error || fallback],
      requestId: null,
      statusCode: null,
    };
  }

  const body = unwrapGeneratedError(error);
  const apiError = asObject(body);
  const detailMessages = extractDetailMessages(apiError?.details);
  const messageMessages = extractMessages(apiError?.message);
  const messages = detailMessages.length > 0 ? detailMessages : messageMessages;
  const message = messages[0] ?? asString(apiError?.error) ?? fallback;

  return {
    body: body ?? null,
    code: asString(apiError?.code),
    error: asString(apiError?.error),
    message,
    messages: messages.length > 0 ? messages : [message],
    requestId: asString(apiError?.requestId),
    statusCode: asNumber(apiError?.statusCode) ?? asNumber(apiError?.status),
  };
}

export function isAuthOrAccessError(error: unknown): boolean {
  const statusCode = normalizeApiError(error).statusCode;
  return statusCode === 401 || statusCode === 403;
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (!error) {
    return fallback;
  }

  return normalizeApiError(error, fallback).message;
}

export function isNotFoundError(error: unknown): boolean {
  return normalizeApiError(error).statusCode === 404;
}
